// @ts-check
/**
 * Free Gift Variant Selector
 *
 * Allows customers to change the variant of a free gift item in cart.
 * Swaps the current free gift line item for the selected variant while
 * preserving all free gift properties. Quantity is always 1.
 */

(function () {
  'use strict';

  let isSwapping = false;

  /**
   * @param {HTMLElement} selectorRoot
   * @returns {Array<{id: number, available: boolean, options: string[], title: string, inventory_quantity: number, inventory_policy: string}>}
   */
  function getVariantsData(selectorRoot) {
    const script = selectorRoot.querySelector('[data-free-gift-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Find a variant matching the selected options.
   * @param {Array<{id: number, available: boolean, options: string[]}>} variants
   * @param {string[]} selectedOptions
   * @returns {{id: number, available: boolean, options: string[]} | null}
   */
  function findVariant(variants, selectedOptions) {
    return variants.find(function (v) {
      return v.options.every(function (opt, idx) {
        return opt === selectedOptions[idx];
      });
    }) || null;
  }

  /**
   * Get selected option values from all selects inside a selector root.
   * @param {HTMLElement} selectorRoot
   * @returns {string[]}
   */
  function getSelectedOptions(selectorRoot) {
    /** @type {string[]} */
    const options = [];
    const selects = selectorRoot.querySelectorAll('[data-option-index]');
    const sorted = Array.from(selects).sort(function (a, b) {
      return parseInt(a.getAttribute('data-option-index') || '0', 10) -
        parseInt(b.getAttribute('data-option-index') || '0', 10);
    });
    for (const sel of sorted) {
      if (sel instanceof HTMLSelectElement) {
        options.push(sel.value);
      }
    }
    return options;
  }

  /**
   * Disable unavailable option combinations in select dropdowns.
   * @param {HTMLElement} selectorRoot
   * @param {Array<{id: number, available: boolean, options: string[]}>} variants
   */
  function updateOptionAvailability(selectorRoot, variants) {
    const selects = selectorRoot.querySelectorAll('[data-option-index]');
    const selectedOptions = getSelectedOptions(selectorRoot);

    for (const sel of selects) {
      if (!(sel instanceof HTMLSelectElement)) continue;
      const optionIndex = parseInt(sel.getAttribute('data-option-index') || '0', 10);

      for (const option of sel.options) {
        const testOptions = [...selectedOptions];
        testOptions[optionIndex] = option.value;

        const matchingVariant = findVariant(variants, testOptions);
        const isAvailable = matchingVariant ? matchingVariant.available : false;

        option.disabled = !isAvailable;
        if (!isAvailable) {
          option.textContent = option.value + ' (Out of Stock)';
        } else {
          option.textContent = option.value;
        }
      }
    }
  }

  /**
   * Show/hide the unavailable message.
   * @param {HTMLElement} selectorRoot
   * @param {boolean} show
   */
  function toggleUnavailableMessage(selectorRoot, show) {
    const msg = selectorRoot.querySelector('.bv-free-gift-variant-selector__unavailable');
    if (msg instanceof HTMLElement) {
      msg.style.display = show ? 'block' : 'none';
    }
  }

  /**
   * Swap the free gift variant in the cart.
   * Removes the current line item and adds the new variant with qty 1.
   * @param {string} currentKey - Current cart line item key
   * @param {number} newVariantId - New variant ID
   */
  async function swapFreeGiftVariant(currentKey, newVariantId) {
    if (isSwapping || !currentKey || !newVariantId) return;
    isSwapping = true;

    /** @type {CartProgressBar|null} */
    const progressBar = /** @type {any} */ (document.querySelector('cart-progress-bar'));
    if (progressBar) progressBar._externalSwapInProgress = true;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentKey, quantity: 0 })
      });

      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newVariantId,
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_gift_message': 'Free Gift - Thank you for your purchase!'
          }
        })
      });

      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      const cart = cartResponse.ok ? await cartResponse.json() : null;

      dispatchCartUpdate(cart);
    } catch (error) {
      console.error('[Free Gift Variant Selector] Error swapping variant:', error);
    } finally {
      setTimeout(function () {
        isSwapping = false;
        if (progressBar) progressBar._externalSwapInProgress = false;
      }, 600);
    }
  }

  /**
   * Dispatch cart:update event for all cart components to re-render.
   * Uses 'free-gift-variant-selector' source so the progress bar skips
   * its add/remove logic during the swap.
   * @param {object|null} cart
   */
  function dispatchCartUpdate(cart) {
    const itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    try {
      /** @type {any} */
      const win = window;
      const CartUpdateEvent = win.CartUpdateEvent ||
        (win.Theme && win.Theme.events && win.Theme.events.CartUpdateEvent);

      if (CartUpdateEvent) {
        document.dispatchEvent(new CartUpdateEvent(cart || {}, 'free-gift-variant-selector', {
          source: 'free-gift-variant-selector',
          itemCount: itemCount
        }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { data: { source: 'free-gift-variant-selector', itemCount: itemCount } }
        }));
      }
    } catch {
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { data: { source: 'free-gift-variant-selector', itemCount: itemCount } }
      }));
    }

    document.dispatchEvent(new CustomEvent('cart:synced', {
      bubbles: true,
      detail: { source: 'free-gift-variant-selector' },
    }));
  }

  /**
   * Handle option change on a free gift variant selector.
   * @param {Event} event
   */
  function handleOptionChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const selectorRoot = select.closest('[data-free-gift-variant-selector]');
    if (!(selectorRoot instanceof HTMLElement)) return;

    const variants = getVariantsData(selectorRoot);
    const selectedOptions = getSelectedOptions(selectorRoot);
    const matchedVariant = findVariant(variants, selectedOptions);

    updateOptionAvailability(selectorRoot, variants);

    if (!matchedVariant || !matchedVariant.available) {
      toggleUnavailableMessage(selectorRoot, true);
      return;
    }

    toggleUnavailableMessage(selectorRoot, false);

    const currentKey = selectorRoot.dataset.currentKey;
    if (!currentKey) return;

    const cartItem = selectorRoot.closest('[data-variant-id]');
    const currentVariantId = cartItem ? cartItem.getAttribute('data-variant-id') : null;

    if (String(matchedVariant.id) === currentVariantId) return;

    selectorRoot.classList.add('bv-free-gift-variant-selector--loading');
    swapFreeGiftVariant(currentKey, matchedVariant.id).finally(function () {
      selectorRoot.classList.remove('bv-free-gift-variant-selector--loading');
    });
  }

  /**
   * Initialize all free gift variant selectors on the page.
   */
  function initSelectors() {
    const selectors = document.querySelectorAll('[data-free-gift-variant-selector]');
    for (const root of selectors) {
      if (!(root instanceof HTMLElement)) continue;
      if (root.dataset.initialized === 'true') continue;

      const variants = getVariantsData(root);
      updateOptionAvailability(root, variants);

      root.addEventListener('change', handleOptionChange);
      root.dataset.initialized = 'true';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSelectors);
  } else {
    initSelectors();
  }

  document.addEventListener('cart:update', function () {
    setTimeout(initSelectors, 300);
  });

  const observer = new MutationObserver(function () {
    const uninitialized = document.querySelectorAll('[data-free-gift-variant-selector]:not([data-initialized="true"])');
    if (uninitialized.length > 0) initSelectors();
  });

  const cartContainers = document.querySelectorAll('.bv-cart-page, .cart-drawer, .bv-mini-cart');
  for (const container of cartContainers) {
    observer.observe(container, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') {
    setTimeout(function () {
      const containers = document.querySelectorAll('.bv-cart-page, .cart-drawer, .bv-mini-cart');
      for (const container of containers) {
        observer.observe(container, { childList: true, subtree: true });
      }
    }, 500);
  }
})();
